<script setup>
import { cloneDeep } from 'lodash-es';
import { computed, onMounted, watch } from 'vue';
import HawkTreeSelect from '~/common/components/organisms/hawk-tree/hawk-treeselect.vue';
import { useCommonImports } from '~/common/composables/common-imports.composable.js';
import { sleep } from '~/common/utils/common.utils';
import { useTerraStore } from '~/terra/store/terra.store';
import { useFeatureActions } from '~/terra/utils/feature-actions.composable.js';
import { useTerraHelperComposable } from '~/terra/utils/helper-composable.js';

const props = defineProps({
  functionCall: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['continue', 'cancel']);

const { $t } = useCommonImports();

const { handleChangeFeatureType } = useFeatureActions();
const terra_store = useTerraStore();

const { getProjectOptions, getStyles } = useTerraHelperComposable();

const filters_ref$ = ref(null);
const state = reactive({
  status: 'not_mounted', // Initial status
  filter_actions: {},
  old_filters: [],
});
const form = ref({
  projects: [],
  target_class: null,
  filters: [],
});

const filter_options = computed(() => {
  const filter_data = terra_store.extra_properties_group_by();
  return Object.entries(filter_data).map(([key, values_map]) => {
    return {
      label: key,
      items: Object.entries(values_map).map(([value, _]) => {
        return { label: value, filter: { key, value }, value: `${key}-$$$$-${value}` };
      }),
    };
  });
});

const projects_list = computed(() => getProjectOptions());
const feature_types = computed(() => {
  return terra_store.ftg.reduce((acc, curr) => {
    if (curr.featureTypes) {
      return acc.concat(curr.featureTypes.filter(val => val.uid).map((val) => {
        return { value: val.uid, label: val.name, color: val.properties.color, feature: val };
      }));
    }
    return acc;
  }, []);
});
const feature_type_name_by_uid = computed(() => {
  return terra_store.ftg.reduce((acc, curr) => {
    if (!acc[curr.name]) {
      acc[curr.name] = {};
    }
    if (curr.featureTypes?.length) {
      curr.featureTypes.forEach((val) => {
        acc[curr.name][val.name] = val.uid;
      });
    }
    return acc;
  }, {});
});
const get_filters = computed(() => cloneDeep(form.value.filters));

// Initialize form with empty projects
function getDefaultSelected() {
  return Object.entries(terra_store.active_projects_map)
    .filter(([_, project]) => project.ortho_enabled || project.features_enabled)
    .map(([uid, _]) => uid);
}

function handleOnChangeFeatureType() {
  emit('continue', {
    response: [
      {
        message: 'Successfully updated the classes.',
      },
    ],
  });
  terra_store.selected_features = [];
}

function onContinue() {
  terra_store.selected_features = terra_store.features_on_map.map(f => terra_store.features_hash[f.properties.uid]);
  handleChangeFeatureType({ uid: form.value.target_class }, handleOnChangeFeatureType, () => (terra_store.selected_features = []));
}

function onCancel() {
  terra_store.selected_features = [];
  emit('cancel', {
    response: [
      {
        message: 'User cancelled the action',
      },
    ],
  });
}

async function updateFilters(filters, active) {
  for (let i = 0; i < filters.length; i++) {
    const { key, value } = filters[i].filter;
    if (terra_store.filters_state.key_values_data?.[key]?.[value]) {
      terra_store.filters_state.key_values_data[key][value].selected = active;
    }
  }
  await sleep(100); // Wait for the state to update
}

watch(() => filters_ref$.value?.getFiltersData(), (filter_actions) => {
  if (state.status === 'not_mounted' && filter_actions && filter_actions?.filter_options !== null) {
    state.filter_actions = filter_actions;
    state.status = 'mounted'; // Update status to mounted
  }
});

watchDebounced(() => [get_filters.value, state.status], async ([filters, status]) => {
  if (status !== 'mounted')
    return;

  const filters_set = new Set(filters.map(filter => filter.value));
  const old_filters_set = new Set(state.old_filters.map(filter => filter.value));

  // Check if both Sets have the same size and the same elements
  const are_filters_same = filters_set.size === old_filters_set.size
    && [...filters_set].every(id => old_filters_set.has(id));

  if (are_filters_same)
    return; // Return early if filters are the same

  // Get values from old_filters_set that are not present in filters_set
  const older_set_values_not_in_filters = [...old_filters_set].filter(
    id => !filters_set.has(id),
  );

  // Filter out disabled filters based on the older set values
  const disabled_filters = state.old_filters.filter(({ value }) =>
    older_set_values_not_in_filters.includes(value),
  );
  await updateFilters(disabled_filters, false);

  // Set active for all now filters
  await updateFilters(filters, true);

  state.filter_actions.updateFeaturesCount();
  terra_store.update_map_features_and_polygon();
  state.old_filters = filters;
}, {
  debounce: 500,
});
onMounted(() => {
  const { target_class, filters } = props.functionCall.arguments;
  const updated_class = target_class[0];
  const feature_type_uid = feature_type_name_by_uid.value
    ?.[updated_class.class_group]
    ?.[updated_class.class] || null;
  form.value.target_class = feature_type_uid;

  form.value.filters = filters.reduce((acc, { filter, values }) => {
    values.forEach((value) => {
      acc.push({ label: value, value: `${filter}-$$$$-${value}`, filter: { key: filter, value } });
    });
    return acc;
  }, []);
});
</script>

<template>
  <NaavixActionBox @continue="onContinue" @cancel="onCancel">
    <template #header>
      <div>
        <div class="flex items-center gap-3 mb-3">
          <HawkFeaturedIcon theme="light-circle" color="primary">
            <IconHawkEditFive class="text-primary-600" />
          </HawkFeaturedIcon>
          <div class="text-md font-semibold text-gray-900">
            {{ $t('Update classes') }}
          </div>
        </div>
        <div class="text-xs text-gray-500">
          {{ $t('Fill the details below and choose a new class to move. Confirm your changes to apply the update.') }}
        </div>
      </div>
    </template>
    <template #content>
      <div>
        <div v-if="state.status === 'not_mounted'" style="position: absolute; top: -9999px; left: -9999px;">
          <TerraFilters ref="filters_ref$" />
        </div>
        <Vueform
          v-model="form"
          size="sm"
          :columns="{
            default: { container: 12, label: 4, wrapper: 12 },
            sm: { container: 12, label: 4, wrapper: 12 },
            md: { container: 12, label: 4, wrapper: 12 },
          }"
          sync
          :display-errors="false"
        >
          <div class="col-span-12">
            <HawkTreeSelect
              class="mb-5"
              :options="{
                name: 'projects',
                label: $t('Layers'),
                openDirection: 'top',
                readOnly: true,
                canClear: false,
              }"
              select_type="LEAF_PRIORITY"
              children_key="projects"
              label_key="name"
              value_key="uid"
              :data="projects_list"
              :initial_state="getDefaultSelected()"
              @update-form="form.projects = $event"
            />
            <SelectElement
              name="target_class"
              :label="$t('Target class')"
              class="mb-5"
              :placeholder="$t('Select')"
              :native="false"
              :items="feature_types"
              :close-on-select="true"
              :can-clear="false"
              open-direction="top"
              :add-classes="{
                SelectElement: {
                  select: {
                    dropdown: '!overflow-y-auto',
                    options: '!none !hidden !h-0',
                    noOptions: '!none !hidden !h-0',
                    noResults: '!none !hidden !h-0',
                  },
                },
              }"
            >
              <template #before-list="{ el$ }">
                <FeatureTypes
                  type="dropdown"
                  :dropdown_value="terra_store.feature_types_by_uid[form.target_class]"
                  @select="($event) => {
                    form.target_class = $event.uid;
                    el$?.input?.close();
                  }"
                />
              </template>
              <template #single-label="{ value }">
                <div class="flex items-center h-full absolute left-0 top-0 pointer-events-none bg-transparent leading-snug pl-3">
                  <div
                    class="cursor-pointer w-3 h-3 rounded-full bg-gray-400"
                    :style="getStyles(value.feature)"
                  />

                  <p
                    class="cursor-pointer pointer-events-auto whitespace-nowrap text-[13px] text-gray-600 ml-2"
                  >
                    <HawkText :content="value.label" :length="20" />
                  </p>
                </div>
              </template>
            </SelectElement>
            <TagsElement
              name="filters"
              :label="$t('Filters')"
              :placeholder="$t('Select')"
              :native="false"
              :hide-selected="false"
              :group-hide-empty="true"
              :group-select="true"
              :groups="true"
              :search="true"
              :object="true"
              :items="filter_options"
              :close-on-select="false"
              open-direction="top"
            />
          </div>
        </Vueform>
      </div>
    </template>
  </NaavixActionBox>
</template>
